/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { ReactElement } from 'react'
import { PublicClientApplication } from '@azure/msal-browser'
import { MsalProvider } from '@azure/msal-react'
import TokenService from '../services/TokenService'
import { getLogger } from '../utils/logger'

const { error: debugError } = getLogger('MSAL')

type MsalAuthProviderProps = {
  children: ReactElement
}

export const msalConfig = {
  auth: {
    clientId: (window as any).REACT_APP_CLIENTID_AZURE_AD,
    authority: (window as any).REACT_APP_AUTH_AZURE_AD,
    redirectUri: (window as any).REACT_APP_AUTH_URL_AZURE_AD,
  },
  cache: {
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      loggerCallback: (level: any, message: string, containsPii: boolean) => {
        if (!containsPii) {
          console.log(`[MSAL] ${message}`)
        }
      },
    },
  },
}

export const loginRequest = {
  scopes: [
    'openid',
    'profile',
    'offline_access', // Required for refresh tokens
    `api://${(window as any).REACT_APP_AZURE_AD_SCOPE}.pepsico.onmicrosoft.com/${(window as any).REACT_APP_CLIENTID_AZURE_AD}/Settings`,
  ],
}

// Singleton MSAL instance used across the application. Exporting it makes
// it accessible from utility hooks (e.g. useAuth) that need to perform
// actions such as logout without instantiating a second PublicClientApplication
// (which would create a separate cache).
const createMsalInstance = (): PublicClientApplication => {
  try {
    return new PublicClientApplication(msalConfig)
  } catch (error) {
    debugError('Failed to create MSAL instance:', error)

    // Clear cache if instance creation fails
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith('msal.')) {
        localStorage.removeItem(key)
      }
    })

    // Retry with fresh cache
    return new PublicClientApplication(msalConfig)
  }
}

export const msalInstance = createMsalInstance()

// Initialize MSAL instance before creating TokenService
let tokenService: TokenService

// Create and export initialization promise with error handling
export const initializeMsal = msalInstance
  .initialize()
  .then(() => {
    tokenService = new TokenService(msalInstance)
    return tokenService
  })
  .catch((error) => {
    debugError('Initialization failed:', error)

    // Check for cache corruption
    if (
      error?.message?.includes('forEach is not a function') ||
      error?.message?.includes('Cannot read properties')
    ) {
      debugError('Cache corruption detected - clearing cache and reloading')

      // Clear corrupted cache directly via localStorage
      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith('msal.')) {
          localStorage.removeItem(key)
        }
      })

      // Force reload to start fresh
      window.location.reload()
    }

    throw error
  })

// Export getter for tokenService
export const getTokenService = () => {
  if (!tokenService) {
    throw new Error('TokenService not initialized. Wait for initializeMsal promise.')
  }
  return tokenService
}

export function MsalAuthProvider({ children }: MsalAuthProviderProps): ReactElement {
  return <MsalProvider instance={msalInstance}>{children}</MsalProvider>
}

// ---------------------------------------------------------------------------
// Optional debugging helpers – available only in non-production builds
// ---------------------------------------------------------------------------

/* eslint-disable @typescript-eslint/no-explicit-any */
const __DEV__ = process.env.NODE_ENV !== 'production'

export const getTokenServiceStats = () => {
  if (!tokenService) return { error: 'TokenService not initialised' }
  return tokenService.getPerformanceStats()
}

if (__DEV__ && typeof window !== 'undefined') {
  ;(window as any).__TOKEN_SERVICE_STATS__ = getTokenServiceStats
  ;(window as any).__RESET_TOKEN_COUNTERS__ = () => {
    if (tokenService) tokenService.resetPerformanceCounters()
  }
  ;(window as any).__GET_TOKEN_SERVICE__ = getTokenService
}
/* eslint-enable @typescript-eslint/no-explicit-any */
